<template>
    <h2 class="display-sans-serif">{{ title }}</h2>
    <div class="mt-6 mb-20 max-w-4xl flex flex-col gap-8">
        <AnalyticsFilters
            :show-date-filters="tabConfig?.conversation_themes_date_filters_enabled"
            :show-k-anonymization-error="themesStatusCode === 451"
            @updated="filters = $event"
            @is-fetching-updated="isFetchingFilters = $event" />
        <TreeMapChart
            class="h-80 md:h-106.25 w-full"
            :title="title"
            :data="mappedThemes"
            :is-loading="isFetchingFilters || isFetchingThemes"
            :format-value="formatValue"
            @click="selectedTheme = $event as ConversationTheme" />
        <TreeMapChart
            class="h-56 md:h-75 w-full"
            :title="selectedTheme?.theme"
            :data="selectedTheme?.sub_themes?.map((st) => remapTheme(st, selectedTheme?.backgroundColor)) || []"
            :is-loading="isFetchingFilters || isFetchingThemes"
            :format-value="formatValue"
            @click="selectedSubTheme = $event as ConversationTheme" />
        <InsightCard v-if="tabConfig?.conversation_themes_examples_enabled && selectedSubTheme?.examples" :is-loading="isFetchingFilters || isFetchingThemes">
            <div class="flex items-center gap-2.5 text-neutral-800">
                <i class="bi bi-pen-fill" aria-hidden="true"></i>
                <span class="title-section">Conversation examples</span>
            </div>
            <hr class="my-4" />
            <div class="flex gap-2.5 text-neutral-800">
                <i class="bi bi-lightbulb-fill" aria-hidden="true"></i>
                <div>
                    <span class="body-bold">{{ selectedSubTheme.theme }}</span>
                    <ul class="list-disc ml-4">
                        <li v-for="(example, index) in selectedSubTheme.examples" :key="index" class="body-small pt-2.5">“{{ example }}”</li>
                    </ul>
                </div>
            </div>
        </InsightCard>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import { useCustomLabels } from "~vue/composables/useCustomLabels";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref, watch } from "vue";

import AnalyticsFilters from "../filters/AnalyticsFilters.vue";
import { getURLParamsFromFilters } from "../filters/helpers";
import InsightCard from "../InsightCard.vue";
import TreeMapChart from "../TreeMapChart.vue";
import type { ClientAnalyticsTabConfig, ConversationTheme, ConversationThemesResponse, SelectedFilters } from "../types";

const tabConfig = inject<ClientAnalyticsTabConfig>("analyticsTabConfig");

const getLabel = useCustomLabels("client_analytics.conversation_themes");
const titleLabel = getLabel();
const title = computed(() => {
    let result = titleLabel.value;
    if (!tabConfig?.conversation_themes_date_filters_enabled) {
        result += " Over Last 6 Months";
    }
    return result;
});

const filters = ref<SelectedFilters>({});
const isFetchingFilters = ref(true);

const themesUrl = ref("");
const {
    data: themesResponse,
    isFetching: isFetchingThemes,
    statusCode: themesStatusCode,
} = useFetch(themesUrl, { immediate: false, refetch: true }).json<ConversationThemesResponse>();

watch(
    filters,
    (newFilters) => {
        const params = getURLParamsFromFilters(newFilters);
        themesUrl.value = "/api/client-analytics/conversation-themes/?" + params.toString();
    },
    { deep: true, immediate: false },
);

const colors = [
    "rgb(145, 211, 225)",
    "rgb(149, 197, 135)",
    "rgb(177, 182, 253)",
    "rgb(223, 248, 182)",
    "rgb(242, 133, 186)",
    "rgb(242, 208, 255)",
    "rgb(248, 240, 182)",
    "rgb(255, 211, 168)",
];

// Helper to map a theme or sub-theme to the format used by TreeMapChart
const remapTheme = (theme: ConversationTheme, backgroundColor?: string) => ({
    label: theme.theme,
    value: theme.percentage,
    backgroundColor,
    extraData: {
        ...theme,
        backgroundColor, // So we can give the selected theme's sub-themes the same color as their parent
    },
});

const mappedThemes = computed(() => {
    if (!themesResponse.value?.themes) {
        return [];
    }

    const themes = themesResponse.value.themes.map((theme, index) => remapTheme(theme, colors[index % colors.length]));

    // Note: Doing this sorting AFTER the color mapping to keep the color/theme pairing consistent
    // even if the sorted theme order changes.
    themes.sort((a, b) => b.extraData.percentage - a.extraData.percentage);
    for (const theme of themes) {
        theme.extraData.sub_themes?.sort((a, b) => b.percentage - a.percentage);
    }

    return themes;
});

const selectedTheme = ref<(ConversationTheme & { backgroundColor?: string }) | null>(null);
const selectedSubTheme = ref<(ConversationTheme & { backgroundColor?: string }) | null>(null);

watch(mappedThemes, () => {
    selectedTheme.value = mappedThemes.value?.[0]?.extraData || null;
});

watch(selectedTheme, () => {
    selectedSubTheme.value = selectedTheme.value?.sub_themes?.[0] || null;
});

watch(
    selectedTheme,
    (newTheme) => {
        if (newTheme) {
            logUserInteraction(AmplitudeEventNames.CLIENT_ANALYTICS_CONVERSATION_THEME_SHOWN, { theme: newTheme.theme });
        }
    },
    { immediate: true },
);

const formatValue = (value: number) => {
    return `${Math.round(value)}%`;
};
</script>
